'use client'

import { forwardRef, ReactNode, useState, useCallback } from 'react'
import { cn } from '../utils/cn'
import { BirhausContainer, type BirhausContainerProps } from './BirhausContainer'
import { BirhausPageHeader, type BirhausPageHeaderProps } from './BirhausPageHeader'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Page Layout Component
 * 
 * Complete page layout system implementing BIRHAUS principles:
 * - Progressive disclosure (header -> content -> sidebar)
 * - Spanish-first navigation and labels
 * - Miller's Law compliance for navigation sections
 * - Accessibility-first semantic structure
 * - Responsive design with mobile-first approach
 * - Cognitive load optimization through layout patterns
 * - Performance-optimized with sticky positioning
 */

export interface BirhausPageLayoutProps {
  // Content sections
  children: ReactNode
  
  // Header configuration
  header?: Omit<BirhausPageHeaderProps, 'ref'>
  
  // Navigation
  breadcrumbs?: ReactNode
  tabs?: ReactNode
  
  // Sidebar (progressive disclosure)
  sidebar?: ReactNode
  sidebarPosition?: 'left' | 'right'
  sidebarWidth?: 'sm' | 'md' | 'lg'
  sidebarSticky?: boolean
  
  // Container configuration
  containerSize?: BirhausContainerProps['size']
  containerPadding?: BirhausContainerProps['padding']
  
  // Layout variants
  variant?: 'default' | 'fullwidth' | 'centered' | 'dashboard'
  
  // Background and styling
  background?: 'default' | 'gray' | 'white' | 'gradient'
  
  // Loading states
  loading?: boolean
  loadingMessage?: string
  loadingMessageEs?: string
  loadingMessageEn?: string
  
  // BIRHAUS Principle #6: Accessibility
  skipToContentLabel?: string
  skipToContentLabelEs?: string
  skipToContentLabelEn?: string
  
  // Locale
  locale?: 'es' | 'en'
  
  // Styling
  className?: string
  headerClassName?: string
  contentClassName?: string
  sidebarClassName?: string
  
  // Layout behavior
  collapsibleSidebar?: boolean
  onSidebarToggle?: (collapsed: boolean) => void
}

export const BirhausPageLayout = forwardRef<HTMLDivElement, BirhausPageLayoutProps>(({
  // Content
  children,
  
  // Header
  header,
  
  // Navigation
  breadcrumbs,
  tabs,
  
  // Sidebar
  sidebar,
  sidebarPosition = 'right',
  sidebarWidth = 'md',
  sidebarSticky = true,
  
  // Container
  containerSize = 'xl',
  containerPadding = 'lg',
  
  // Layout
  variant = 'default',
  background = 'default',
  
  // Loading
  loading = false,
  loadingMessage,
  loadingMessageEs = 'Cargando...',
  loadingMessageEn = 'Loading...',
  
  // Accessibility
  skipToContentLabel,
  skipToContentLabelEs = 'Saltar al contenido principal',
  skipToContentLabelEn = 'Skip to main content',
  
  // Locale
  locale = 'es',
  
  // Styling
  className,
  headerClassName,
  contentClassName,
  sidebarClassName,
  
  // Behavior
  collapsibleSidebar = false,
  onSidebarToggle,
  
  ...props
}, ref) => {
  // Local state for collapsible sidebar
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const getDisplayText = useCallback((
    primary: string | undefined,
    spanish: string | undefined,
    english: string | undefined,
    fallback: string
  ): string => {
    if (locale === 'es') {
      return primary || spanish || english || fallback
    }
    return primary || english || spanish || fallback
  }, [locale])

  // Handle sidebar toggle
  const handleSidebarToggle = useCallback(() => {
    const newCollapsed = !sidebarCollapsed
    setSidebarCollapsed(newCollapsed)
    onSidebarToggle?.(newCollapsed)
  }, [sidebarCollapsed, onSidebarToggle])

  // Background styles
  const backgroundClasses = {
    default: 'bg-gray-50',
    gray: 'bg-gray-100',
    white: 'bg-white',
    gradient: 'bg-gradient-to-br from-gray-50 to-gray-100'
  }

  // Sidebar width classes
  const sidebarWidthClasses = {
    sm: 'w-64',  // 256px
    md: 'w-80',  // 320px
    lg: 'w-96'   // 384px
  }

  // Variant-specific styles
  const variantClasses = {
    default: {
      layout: 'min-h-screen',
      content: 'flex-1'
    },
    fullwidth: {
      layout: 'min-h-screen',
      content: 'flex-1'
    },
    centered: {
      layout: 'min-h-screen flex items-center justify-center',
      content: 'w-full max-w-4xl'
    },
    dashboard: {
      layout: 'min-h-screen',
      content: 'flex-1'
    }
  }

  const variantConfig = safeAccess(variantClasses, variant, variantClasses.default)

  // Skip link (BIRHAUS Principle #6: Accessibility)
  const skipLabel = getDisplayText(
    skipToContentLabel,
    skipToContentLabelEs,
    skipToContentLabelEn,
    skipToContentLabelEs
  )

  const displayLoadingMessage = getDisplayText(
    loadingMessage,
    loadingMessageEs,
    loadingMessageEn,
    loadingMessageEs
  )

  // Loading state
  if (loading) {
    return (
      <div
        ref={ref}
        className={cn(
          'min-h-screen flex items-center justify-center',
          safeAccess(backgroundClasses, background, backgroundClasses.default),
          className
        )}
        role="status"
        aria-live="polite"
        {...props}
      >
        <div className="text-center space-y-4">
          <div className="animate-spin w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full mx-auto" />
          <p className="text-gray-600">{displayLoadingMessage}</p>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={cn(
        'birhaus-page-layout',
        variantConfig.layout,
        safeAccess(backgroundClasses, background, backgroundClasses.default),
        className
      )}
      {...props}
    >
      {/* Skip to content link (BIRHAUS Accessibility) */}
      <a
        href="#birhaus-main-content"
        className={cn(
          'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4',
          'bg-blue-600 text-white px-4 py-2 rounded-lg font-medium',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
          'z-50 transition-all duration-200'
        )}
      >
        {skipLabel}
      </a>

      {/* Header Section */}
      {(header || breadcrumbs || tabs) && (
        <div className={cn(
          'bg-white border-b border-gray-200 relative z-10',
          headerClassName
        )}>
          <BirhausContainer size={containerSize} padding={containerPadding}>
            {/* Breadcrumbs */}
            {breadcrumbs && (
              <div className="mb-6">
                {breadcrumbs}
              </div>
            )}
            
            {/* Page Header */}
            {header && (
              <BirhausPageHeader
                {...header}
                locale={locale}
                className={cn('mb-0', header.className)}
              />
            )}
            
            {/* Tabs Navigation */}
            {tabs && (
              <div className="mt-6 border-t border-gray-100 pt-6">
                {tabs}
              </div>
            )}
          </BirhausContainer>
        </div>
      )}

      {/* Main Content Area */}
      <div className={variantConfig.content}>
        <BirhausContainer 
          size={variant === 'fullwidth' ? 'full' : containerSize} 
          padding={containerPadding}
        >
          <div className={cn(
            'flex gap-6',
            variant === 'centered' && 'justify-center',
            contentClassName
          )}>
            {/* Left Sidebar */}
            {sidebar && sidebarPosition === 'left' && (
              <aside className={cn(
                'flex-shrink-0 transition-all duration-300',
                safeAccess(sidebarWidthClasses, sidebarWidth, sidebarWidthClasses.md),
                collapsibleSidebar && sidebarCollapsed && 'w-16',
                sidebarClassName
              )}>
                <div className={cn(
                  sidebarSticky && 'sticky top-6'
                )}>
                  {/* Collapse/Expand Button */}
                  {collapsibleSidebar && (
                    <button
                      onClick={handleSidebarToggle}
                      className={cn(
                        'mb-4 p-2 rounded-lg bg-white border border-gray-200',
                        'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500',
                        'transition-colors duration-200'
                      )}
                      aria-label={
                        locale === 'es' 
                          ? (sidebarCollapsed ? 'Expandir sidebar' : 'Contraer sidebar')
                          : (sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar')
                      }
                    >
                      <div className={cn(
                        'w-4 h-4 transition-transform duration-200',
                        sidebarCollapsed && 'rotate-180'
                      )}>
                        {'◀'}
                      </div>
                    </button>
                  )}
                  {sidebar}
                </div>
              </aside>
            )}

            {/* Main Content */}
            <main 
              id="birhaus-main-content"
              className={cn(
                'flex-1',
                sidebar ? 'min-w-0' : '',
                variant === 'centered' && 'max-w-none'
              )}
              role="main"
            >
              {children}
            </main>

            {/* Right Sidebar */}
            {sidebar && sidebarPosition === 'right' && (
              <aside className={cn(
                'flex-shrink-0 transition-all duration-300',
                safeAccess(sidebarWidthClasses, sidebarWidth, sidebarWidthClasses.md),
                collapsibleSidebar && sidebarCollapsed && 'w-16',
                sidebarClassName
              )}>
                <div className={cn(
                  sidebarSticky && 'sticky top-6'
                )}>
                  {/* Collapse/Expand Button */}
                  {collapsibleSidebar && (
                    <button
                      onClick={handleSidebarToggle}
                      className={cn(
                        'mb-4 p-2 rounded-lg bg-white border border-gray-200',
                        'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500',
                        'transition-colors duration-200'
                      )}
                      aria-label={
                        locale === 'es' 
                          ? (sidebarCollapsed ? 'Expandir sidebar' : 'Contraer sidebar')
                          : (sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar')
                      }
                    >
                      <div className={cn(
                        'w-4 h-4 transition-transform duration-200',
                        sidebarCollapsed && 'rotate-180'
                      )}>
                        {'▶'}
                      </div>
                    </button>
                  )}
                  {sidebar}
                </div>
              </aside>
            )}
          </div>
        </BirhausContainer>
      </div>
    </div>
  )
})

BirhausPageLayout.displayName = 'BirhausPageLayout'

// Hook for managing page layout state
export function useBirhausPageLayout() {
  const [sidebarVisible, setSidebarVisible] = useState(true)
  const [currentTab, setCurrentTab] = useState<string | null>(null)
  
  const toggleSidebar = useCallback(() => {
    setSidebarVisible(prev => !prev)
  }, [])
  
  const hideSidebar = useCallback(() => {
    setSidebarVisible(false)
  }, [])
  
  const showSidebar = useCallback(() => {
    setSidebarVisible(true)
  }, [])
  
  return {
    // Sidebar state
    sidebarVisible,
    setSidebarVisible,
    toggleSidebar,
    hideSidebar,
    showSidebar,
    
    // Tab state
    currentTab,
    setCurrentTab,
    
    // Layout utilities
    getResponsiveContainer: (isMobile: boolean) => isMobile ? 'sm' : 'xl',
    getResponsivePadding: (isMobile: boolean) => isMobile ? 'sm' : 'lg'
  }
}

// Pre-configured layout templates for common page types
export const birhausPageLayoutPresets = {
  // Dashboard layout
  dashboard: {
    variant: 'dashboard' as const,
    background: 'default' as const,
    containerSize: 'xl' as const,
    header: {
      variant: 'hero' as const,
      gradient: 'blue' as const,
      titleEs: 'Panel de Control',
      titleEn: 'Dashboard'
    }
  },
  
  // List/Table view
  listView: {
    variant: 'default' as const,
    background: 'default' as const,
    containerSize: 'xl' as const,
    header: {
      variant: 'default' as const,
      gradient: 'indigo' as const
    },
    sidebarPosition: 'left' as const,
    sidebarWidth: 'sm' as const
  },
  
  // Detail/Form view
  detailView: {
    variant: 'default' as const,
    background: 'white' as const,
    containerSize: 'lg' as const,
    header: {
      variant: 'compact' as const,
      gradient: 'gray' as const
    }
  },
  
  // Settings pages
  settings: {
    variant: 'default' as const,
    background: 'gray' as const,
    containerSize: 'lg' as const,
    header: {
      variant: 'minimal' as const,
      titleEs: 'Configuración',
      titleEn: 'Settings'
    },
    sidebarPosition: 'left' as const,
    sidebarWidth: 'sm' as const
  },
  
  // Report/Analytics
  analytics: {
    variant: 'dashboard' as const,
    background: 'gradient' as const,
    containerSize: 'xl' as const,
    header: {
      variant: 'hero' as const,
      gradient: 'emerald' as const,
      titleEs: 'Análisis',
      titleEn: 'Analytics'
    }
  },
  
  // Modal/Dialog content
  modal: {
    variant: 'centered' as const,
    background: 'white' as const,
    containerSize: 'md' as const,
    containerPadding: 'md' as const,
    header: {
      variant: 'minimal' as const
    }
  }
}